import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import styled, { useTheme } from "styled-components";
import { AnalysisAPI } from "../../../../../api/index.js";
import {
  Button,
  Flyout,
  FlyoutHeader,
  FlyoutTitle,
} from "@monolith-forensics/monolith-ui";
import { EditIcon, TrashIcon } from "lucide-react";
import FlyoutDetailsTab from "./FlyoutDetailsTab.js";
import {
  DeleteTraceAttributeModal,
  EditTraceAttributeModal,
} from "@/components/Modals";
import { useParams } from "react-router-dom";
import TraceAttributeActivity from "./TraceAttributeActivity.js";

const tabDefs = [
  {
    label: "Details",
    value: "details",
  },
  {
    label: "Activity",
    value: "activity",
  },
];

const TabPanel = styled(({ className, children, value }) => {
  return <div className={className}>{children}</div>;
})`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
`;

TabPanel.displayName = "TabPanel";

const Tabs = styled(({ className, children, defaultValue, onChange }) => {
  if (!Array.isArray(children)) children = [children]; // Ensure children is an array
  const [activeTab, setActiveTab] = useState(defaultValue || "details");

  const handleChange = (tab) => {
    setActiveTab(tab);
    onChange?.(tab);
  };

  const tabPanels = children.filter(
    (child) => child?.type?.displayName === "TabPanel"
  );

  const activePanel = tabPanels.find(
    (child) => child.props.value === activeTab
  );

  return (
    <div className={className}>
      <div className="tabs-list">
        {tabDefs.map((tab) => (
          <div
            className={`tab`}
            data-active={activeTab === tab.value}
            onClick={() => handleChange(tab.value)}
            key={tab.value}
          >
            {tab.label}
          </div>
        ))}
      </div>
      <div className="tab-content">{activePanel}</div>
    </div>
  );
})`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;

  .tabs-list {
    user-select: none;
    cursor: pointer;
    display: flex;
    flex-direction: row;
    color: ${({ theme }) => theme.palette.text.secondary};
    border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
  }

  .tab {
    padding-right: 10px;
    padding-left: 10px;
    padding-top: 5px;
    padding-bottom: 5px;
    border-bottom: 1px solid transparent;
  }

  .tab:hover {
    background-color: ${({ theme }) => theme.palette.action.hover};
  }

  .tab[data-active="true"] {
    border-bottom-color: ${({ theme }) => theme.palette.primary.main};
    color: ${({ theme }) => theme.palette.primary.main};
  }

  .tab-content {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    height: 0px;
  }
`;

const TraceAttributeFlyout = ({
  open = false,
  onClose,
  uuid = "",
  defaultData = {},
  onEdit,
  onDelete,
}) => {
  const { case_id: caseID } = useParams();
  const case_id = parseInt(caseID, 10);
  const theme = useTheme();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["trace-attributes", "list", { uuid }],
    queryFn: ({ queryKey }) => {
      const [_, __, query] = queryKey;
      return AnalysisAPI.TraceAttributes.get(query);
    },
    enabled: !!uuid,
    initialData: [defaultData],
  });

  const itemData = data?.data?.[0] || {};

  return (
    <Flyout open={open} onClose={() => onClose?.()}>
      {isLoading && <div>Loading...</div>}
      {!isLoading && (
        <>
          <FlyoutHeader>
            <FlyoutTitle>{`${itemData.label} → ${itemData.value}`}</FlyoutTitle>
            <EditTraceAttributeModal
              show={showEditModal}
              defaultFormData={{
                case_id,
                uuid: itemData?.uuid,
                type: itemData?.type,
                value: itemData?.value,
                category: itemData?.category,
                description: itemData?.description,
              }}
              onClose={() => setShowEditModal(false)}
              onCancel={() => setShowEditModal(false)}
              onSubmit={(data) => {
                refetch();
                onEdit?.(data);
                setShowEditModal(false);
              }}
            />
            <DeleteTraceAttributeModal
              show={showDeleteModal}
              defaultFormData={{ ...itemData, case_id }}
              onClose={() => setShowDeleteModal(false)}
              onCancel={() => setShowDeleteModal(false)}
              onSubmit={(data) => {
                onDelete?.(data);
                setShowDeleteModal(false);
              }}
            />
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                gap: 10,
                alignItems: "center",
                color: theme.palette.text.secondary,
                fontWeight: 500,
                marginBottom: 5,
              }}
            >
              <div>Trace Attribute</div>
              <Button
                size="xxs"
                variant="text"
                leftSection={<EditIcon size={14} />}
                color="primary"
                onClick={() => setShowEditModal(true)}
              >
                Edit
              </Button>
              <Button
                size="xxs"
                variant="text"
                leftSection={<TrashIcon size={14} />}
                color="error"
                onClick={() => setShowDeleteModal(true)}
              >
                Delete
              </Button>
            </div>
          </FlyoutHeader>
          <Tabs>
            <TabPanel value="details">
              <FlyoutDetailsTab defaultData={itemData} />
            </TabPanel>
            <TabPanel value="activity">
              <TraceAttributeActivity defaultData={itemData} />
            </TabPanel>
          </Tabs>
        </>
      )}
    </Flyout>
  );
};

export default TraceAttributeFlyout;
